import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { brandPayment, formatMoney } from 'src/services/brandPaymentService';
import { CommonHelper } from 'src/utils/commonHelper';

export default function ResellerPayouts({ onClaimSuccess }) {
  const { t } = useTranslation();
  const allowed = CommonHelper.GetBrandBillingAccess().canManage;
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [claim, setClaim] = useState(null);
  const requestKeys = useRef({});
  const generation = useRef(0);
  const load = useCallback(async () => {
    if (!allowed || !/^\d{4}-\d{2}$/.test(month)) return;
    const current = ++generation.current;
    setLoading(true);
    try {
      const result = await brandPayment.payouts(month);
      if (generation.current === current) {
        setData(result);
        setEmail(result.profile.paypal_email || '');
      }
    } catch (e) {
      if (generation.current === current) setError(e.message);
    } finally {
      if (generation.current === current) setLoading(false);
    }
  }, [allowed, month]);
  useEffect(() => {
    setData(null);
    setError('');
    setMessage('');
    load();
    return () => {
      generation.current += 1;
    };
  }, [load]);
  const save = async () => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      await brandPayment.savePayoutProfile(email.trim());
      setEmailOpen(false);
      await load();
      setMessage(t('billing_payout.paypal_email_saved_existing_requests_keep_their_original_payout_email'));
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const submitClaim = async () => {
    setBusy(true);
    setError('');
    setMessage('');
    const key = `${month}:${claim.currency}`;
    requestKeys.current[key] = requestKeys.current[key] || crypto.randomUUID();
    try {
      await brandPayment.claimPayout({
        month,
        currency: claim.currency,
        request_key: requestKeys.current[key],
      });
      delete requestKeys.current[key];
      setClaim(null);
      if (onClaimSuccess) {
        onClaimSuccess();
        return;
      }
      setMessage(t('billing_payout.payout_request_submitted_for_manual_payment_no_money_has_been_transferred_yet'));
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  if (!allowed)
    return (
      <Alert severity="info">{t('billing_payout.only_reseller_organization_administrators_can_view_payouts')}</Alert>
    );
  const dirty = email.trim() !== (data?.profile.paypal_email || '');
  return (
    <Stack spacing={3} sx={{ p: 2, minWidth: 0 }}>
      <Typography variant="h5">{t('billing_payout.brand_payments_and_payout_claims')}</Typography>
      <Typography>
        {t('billing_payout.commission_description', {
          rate: data ? `${data.commission_percent}%` : t('billing_payout.loading'),
        })}
      </Typography>
      {error && <Alert severity="error">{error}</Alert>}
      {message && <Alert severity="success">{message}</Alert>}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          label={t('billing_payout.payment_month_utc')}
          type="month"
          value={month}
          disabled={busy}
          onChange={(e) => setMonth(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <Button onClick={load} disabled={busy || loading}>
          {t('billing_payout.refresh')}
        </Button>
      </Stack>
      {loading && <CircularProgress aria-label={t('billing_payout.loading_payout_details')} />}
      {data && !loading && (
        <>
          <Card>
            <CardContent>
              <Stack spacing={2}>
                <Typography variant="h6">{t('billing_payout.paypal_payout_email')}</Typography>
                <Typography>{data.profile.paypal_email || t('billing_payout.no_paypal_email_added')}</Typography>
                <Button
                  variant="outlined"
                  disabled={busy}
                  onClick={() => {
                    setEmail(data.profile.paypal_email || '');
                    setError('');
                    setEmailOpen(true);
                  }}
                >
                  {data.profile.paypal_email
                    ? t('billing_payout.edit_paypal_email')
                    : t('billing_payout.add_paypal_email')}
                </Button>
              </Stack>
            </CardContent>
          </Card>
          {data.summary.length === 0 && (
            <Alert severity="info">{t('billing_payout.no_successful_brand_payments_in_this_month')}</Alert>
          )}
          {data.summary.map((v) => (
            <Card key={v.currency}>
              <CardContent>
                <Stack spacing={1}>
                  <Typography variant="h6">
                    {t('billing_payout.currency_earnings', { currency: v.currency })}
                  </Typography>
                  <Typography>
                    {t('billing_payout.successful_payments')}: {formatMoney(v.gross, v.currency)}
                  </Typography>
                  <Typography>
                    {t('billing_payout.commission')}: {formatMoney(v.commission, v.currency)}
                  </Typography>
                  <Typography>
                    {t('billing_payout.your_earnings')}: {formatMoney(v.net, v.currency)}
                  </Typography>
                  <Typography>
                    {t('billing_payout.available_to_claim')}: {formatMoney(v.available, v.currency)}
                  </Typography>
                  <Typography>
                    {t('billing_payout.requested')}: {formatMoney(v.requested, v.currency)} |{' '}
                    {t('billing_payout.paid_out')}: {formatMoney(v.paid, v.currency)}
                  </Typography>
                  <Button
                    variant="contained"
                    disabled={busy || dirty || !data.profile.paypal_email || v.available <= 0}
                    onClick={() => setClaim(v)}
                  >
                    {t('billing_payout.request_payout')}
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          ))}
          <Typography variant="h6">{t('billing_payout.successful_monthly_payments')}</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {[
                    t('billing_payout.brand'),
                    t('billing_payout.invoice'),
                    t('billing_payout.paid_at'),
                    t('billing_payout.received'),
                    t('billing_payout.commission'),
                    t('billing_payout.your_share'),
                    t('billing_payout.claim'),
                  ].map((h) => (
                    <TableCell key={h}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.payments.map((v) => (
                  <TableRow key={v.id}>
                    <TableCell>{v.brand_name}</TableCell>
                    <TableCell>{v.invoice}</TableCell>
                    <TableCell>{new Date(v.paid_at).toLocaleString()}</TableCell>
                    <TableCell>{formatMoney(v.gross, v.currency)}</TableCell>
                    <TableCell>
                      {formatMoney(v.commission, v.currency)} ({v.commission_percent}%)
                    </TableCell>
                    <TableCell>{formatMoney(v.net, v.currency)}</TableCell>
                    <TableCell>
                      {v.claim_id
                        ? v.claim_status === 'paid'
                          ? t('billing_payout.paid')
                          : t('billing_payout.awaiting_manual_payout')
                        : t('billing_payout.available')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="h6">{t('billing_payout.claims_for_this_month')}</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {[
                    t('billing_payout.paypal_email'),
                    t('billing_payout.amount'),
                    t('billing_payout.status'),
                    t('billing_payout.requested'),
                    t('billing_payout.paid'),
                    t('billing_payout.payment_reference'),
                  ].map((h) => (
                    <TableCell key={h}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.claims.map((v) => (
                  <TableRow key={v.id}>
                    <TableCell>{v.paypal_email}</TableCell>
                    <TableCell>{formatMoney(v.net, v.currency)}</TableCell>
                    <TableCell>
                      {v.status === 'requested' ? t('billing_payout.awaiting_manual_payout') : t('billing_payout.paid')}
                    </TableCell>
                    <TableCell>{new Date(v.requested_at).toLocaleString()}</TableCell>
                    <TableCell>{v.paid_at ? new Date(v.paid_at).toLocaleString() : '-'}</TableCell>
                    <TableCell>{v.reference || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
      <Dialog
        open={emailOpen}
        onClose={
          busy
            ? undefined
            : () => {
                setEmailOpen(false);
                setEmail(data?.profile.paypal_email || '');
              }
        }
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {data?.profile.paypal_email ? t('billing_payout.edit_paypal_email') : t('billing_payout.add_paypal_email')}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            {t('billing_payout.enter_the_paypal_account_email_for_manual_payouts_saving_it_does_not_connect_or_verif')}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label={t('billing_payout.paypal_email')}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={busy}
          />
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            disabled={busy}
            onClick={() => {
              setEmailOpen(false);
              setEmail(data?.profile.paypal_email || '');
            }}
          >
            {t('billing_payout.cancel')}
          </Button>
          <Button variant="contained" disabled={busy || !dirty || !email.trim()} onClick={save}>
            {t('billing_payout.save_paypal_email')}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={!!claim} onClose={busy ? undefined : () => setClaim(null)}>
        <DialogTitle>{t('billing_payout.request_manual_payout')}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {t('billing_payout.confirm_payout', {
              amount: claim ? formatMoney(claim.available, claim.currency) : '',
              month,
              email: data?.profile.paypal_email || '',
            })}
          </Box>
          {error && <Alert severity="error">{error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setClaim(null)}>
            {t('billing_payout.cancel')}
          </Button>
          <Button disabled={busy} onClick={submitClaim}>
            {t('billing_payout.submit_request')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
